import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Settings as SettingsIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import ModernLayout from './common/ModernLayout';
import ModernCard from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';
import axiosInstance from './axiosConfig';
import { QRCodeCanvas } from 'qrcode.react';
import moment from 'moment';
import { useLanguage } from '../contexts/LanguageContext';

const RoomDisplayManager = () => {
  const { translate } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [selectedRoomForQR, setSelectedRoomForQR] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    displayEnabled: false,
    displayPassword: '',
    refreshInterval: 30,
    showSettings: true,
    showEquipment: true,
    showProgress: true,
    customMessage: '',
    theme: 'default'
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchRooms(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const response = await axiosInstance.get(`/api/projects/${userId}`);
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchRooms = async (projectId) => {
    try {
      const response = await axiosInstance.get(`/api/rooms/${projectId}`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    }
  };

  const handleOpenDialog = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        name: room.name,
        displayEnabled: room.displayEnabled || false,
        displayPassword: room.displayPassword || '',
        refreshInterval: room.refreshInterval || 30,
        showSettings: room.showSettings !== false,
        showEquipment: room.showEquipment !== false,
        showProgress: room.showProgress !== false,
        customMessage: room.customMessage || '',
        theme: room.theme || 'default'
      });
    } else {
      setEditingRoom(null);
      setFormData({
        name: '',
        displayEnabled: false,
        displayPassword: '',
        refreshInterval: 30,
        showSettings: true,
        showEquipment: true,
        showProgress: true,
        customMessage: '',
        theme: 'default'
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    try {
      const roomData = {
        ...formData,
        projectId: selectedProject
      };

      if (editingRoom) {
        await axiosInstance.put(`/api/rooms/${editingRoom.id}/display-config`, roomData);
        showSnackbar(translate('roomDisplayManager.configUpdated'), 'success');
      } else {
        await axiosInstance.post('/api/rooms', roomData);
        showSnackbar(translate('roomDisplayManager.roomCreated'), 'success');
      }

      setOpenDialog(false);
      fetchRooms(selectedProject);
    } catch (error) {
      console.error('Error saving room:', error);
      showSnackbar(translate('roomDisplayManager.configError'), 'error');
    }
  };

  const handleDelete = async (roomId) => {
    if (window.confirm(translate('roomDisplayManager.confirmDelete'))) {
      try {
        await axiosInstance.delete(`/api/rooms/${roomId}`);
        showSnackbar(translate('roomDisplayManager.roomDeleted'), 'success');
        fetchRooms(selectedProject);
      } catch (error) {
        console.error('Error deleting room:', error);
        showSnackbar(translate('roomDisplayManager.deleteError'), 'error');
      }
    }
  };

  const handleToggleDisplay = async (roomId, enabled) => {
    try {
      await axiosInstance.patch(`/api/rooms/${roomId}/display-config`, {
        displayEnabled: enabled
      });
      showSnackbar(translate('roomDisplayManager.displayToggled'), 'success');
      fetchRooms(selectedProject);
    } catch (error) {
      console.error('Error toggling display:', error);
      showSnackbar(translate('roomDisplayManager.displayToggleError'), 'error');
    }
  };

  const handleShowQR = (room) => {
    setSelectedRoomForQR(room);
    setOpenQRDialog(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar(translate('roomDisplayManager.urlCopied'), 'success');
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getDisplayUrl = (room) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/room-display/${room.id}`;
  };

  const getStatusColor = (room) => {
    if (!room.displayEnabled) return 'default';
    return room.lastUpdate && moment().diff(moment(room.lastUpdate), 'minutes') < 5 ? 'success' : 'warning';
  };

  return (
    <ModernLayout title={translate('roomDisplayManager.title')}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {translate('roomDisplayManager.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('roomDisplayManager.subtitle')}
        </Typography>
      </Box>

      {/* Project Selection */}
      <ModernCard
        title="Seleccionar Proyecto"
        subtitle="Elige el proyecto para gestionar sus rooms"
        sx={{ mb: 3 }}
      >
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {projects.map((project) => (
            <Chip
              key={project.id}
              label={project.name}
              variant={selectedProject === project.id ? 'filled' : 'outlined'}
              color={selectedProject === project.id ? 'primary' : 'default'}
              onClick={() => setSelectedProject(project.id)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </ModernCard>

      {/* Rooms Table */}
      <ModernCard
        title={translate('roomDisplayManager.roomsList')}
        subtitle={`${translate('roomDisplayManager.showing')} ${rooms.length} ${translate('roomDisplayManager.rooms')}`}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {rooms.length} Rooms encontrados
          </Typography>
          <GradientButton
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Crear Nuevo Room
          </GradientButton>
        </Box>

        <TableContainer component={Paper} sx={{ background: 'rgba(255, 255, 255, 0.9)' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{translate('roomDisplayManager.room')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('roomDisplayManager.project')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('roomDisplayManager.status')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('roomDisplayManager.lastUpdate')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('roomDisplayManager.configuration')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{translate('roomDisplayManager.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {room.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {room.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {room.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{room.project?.name || translate('roomDisplayManager.noProject')}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={room.displayEnabled ? translate('roomDisplayManager.active') : translate('roomDisplayManager.inactive')}
                        color={getStatusColor(room)}
                        size="small"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={room.displayEnabled || false}
                            onChange={(e) => handleToggleDisplay(room.id, e.target.checked)}
                            size="small"
                          />
                        }
                        label=""
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {room.lastUpdate ? moment(room.lastUpdate).format('DD/MM/YYYY HH:mm') : translate('roomDisplayManager.never')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={`Refresh: ${room.refreshInterval || 30}s`}
                        size="small"
                        variant="outlined"
                      />
                      {room.displayPassword && (
                        <Chip
                          label={translate('roomDisplayManager.protected')}
                          size="small"
                          color="warning"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={translate('roomDisplayManager.editConfiguration')}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(room)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('roomDisplayManager.viewDisplay')}>
                        <IconButton
                          size="small"
                          onClick={() => window.open(getDisplayUrl(room), '_blank')}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={translate('roomDisplayManager.deleteRoom')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(room.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </ModernCard>

      {/* Configuration Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 700,
          fontSize: '1.3rem',
        }}>
          {translate('roomDisplayManager.configureDisplay')} - {editingRoom?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('roomDisplayManager.roomName')}
                  fullWidth
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('roomDisplayManager.displayPassword')}
                  fullWidth
                  type="password"
                  value={formData.displayPassword}
                  onChange={(e) => setFormData({...formData, displayPassword: e.target.value})}
                  helperText={translate('roomDisplayManager.passwordHelper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('roomDisplayManager.updateInterval')}
                  fullWidth
                  type="number"
                  value={formData.refreshInterval}
                  onChange={(e) => setFormData({...formData, refreshInterval: parseInt(e.target.value)})}
                  inputProps={{ min: 10, max: 300 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={translate('roomDisplayManager.theme')}
                  fullWidth
                  select
                  value={formData.theme}
                  onChange={(e) => setFormData({...formData, theme: e.target.value})}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                >
                  <option value="default">{translate('roomDisplayManager.defaultTheme')}</option>
                  <option value="dark">{translate('roomDisplayManager.darkTheme')}</option>
                  <option value="light">{translate('roomDisplayManager.lightTheme')}</option>
                  <option value="greenhouse">{translate('roomDisplayManager.greenhouseTheme')}</option>
                </TextField>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                  {translate('roomDisplayManager.displayOptions')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showSettings}
                      onChange={(e) => setFormData({...formData, showSettings: e.target.checked})}
                    />
                  }
                  label={translate('roomDisplayManager.showSettingsButton')}
                  sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showEquipment}
                      onChange={(e) => setFormData({...formData, showEquipment: e.target.checked})}
                    />
                  }
                  label={translate('roomDisplayManager.showEquipment')}
                  sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.showProgress}
                      onChange={(e) => setFormData({...formData, showProgress: e.target.checked})}
                    />
                  }
                  label={translate('roomDisplayManager.showProgress')}
                  sx={{ color: 'rgba(0, 0, 0, 0.87)' }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label={translate('roomDisplayManager.customMessage')}
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.customMessage}
                  onChange={(e) => setFormData({...formData, customMessage: e.target.value})}
                  helperText={translate('roomDisplayManager.customMessageHelper')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.23)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(0, 0, 0, 0.4)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.7)',
                    },
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ 
              textTransform: 'none',
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {translate('roomDisplayManager.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            sx={{
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            {translate('roomDisplayManager.saveConfiguration')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog 
        open={openQRDialog} 
        onClose={() => setOpenQRDialog(false)}
        PaperProps={{
          sx: {
            background: 'white',
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          fontWeight: 700,
          fontSize: '1.3rem',
        }}>
          QR Code para {selectedRoomForQR?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ textAlign: 'center', p: 2 }}>
            {selectedRoomForQR && (
              <QRCodeCanvas
                value={getDisplayUrl(selectedRoomForQR)}
                size={256}
                level="H"
                includeMargin={true}
              />
            )}
            <Typography variant="body2" sx={{ 
              mt: 2, 
              fontFamily: 'monospace',
              color: 'rgba(0, 0, 0, 0.87)',
            }}>
              {selectedRoomForQR && getDisplayUrl(selectedRoomForQR)}
            </Typography>
            <Button
              startIcon={<CopyIcon />}
              onClick={() => selectedRoomForQR && copyToClipboard(getDisplayUrl(selectedRoomForQR))}
              sx={{ 
                mt: 2,
                textTransform: 'none',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                },
              }}
            >
              {translate('roomDisplayManager.copyUrl')}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setOpenQRDialog(false)}
            sx={{ 
              textTransform: 'none',
              color: 'rgba(0, 0, 0, 0.7)',
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {translate('roomDisplayManager.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({...snackbar, open: false})}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ModernLayout>
  );
};

export default RoomDisplayManager; 